import { useForm } from "@inertiajs/react"
import { type FormEventHandler, useRef } from "react"

import HeadingSmall from "@/components/heading-small"
import InputError from "@/components/input-error"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { usersPath } from "@/routes"

interface DeleteUserProps {
  email: string
}

interface DeleteUserForm {
  email_confirmation: string
}

export default function DeleteUser({ email }: DeleteUserProps) {
  const emailInput = useRef<HTMLInputElement>(null)
  const {
    data,
    setData,
    delete: destroy,
    processing,
    reset,
    errors,
    clearErrors,
  } = useForm<Required<DeleteUserForm>>({ email_confirmation: "" })

  const deleteUser: FormEventHandler = (e) => {
    e.preventDefault()

    destroy(usersPath(), {
      preserveScroll: true,
      onSuccess: () => closeModal(),
      onError: () => emailInput.current?.focus(),
      onFinish: () => reset(),
    })
  }

  const closeModal = () => {
    clearErrors()
    reset()
  }

  const isEmailMatch = data.email_confirmation === email

  return (
    <div className="space-y-6">
      <HeadingSmall
        title="Delete account"
        description="Delete your account and all of its resources"
      />
      <div className="space-y-4 rounded-lg border border-red-200/10 bg-red-700/10 p-4">
        <div className="relative space-y-0.5 text-red-100">
          <p className="font-medium">Warning</p>
          <p className="text-sm">
            Please proceed with caution, this cannot be undone.
          </p>
        </div>

        <Dialog>
          <DialogTrigger asChild>
            <Button variant="destructive">Delete account</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogTitle>
              Are you sure you want to delete your account?
            </DialogTitle>
            <DialogDescription>
              Once your account is deleted, all of its resources and data will
              also be permanently deleted. Please type{" "}
              <span className="text-foreground font-semibold">{email}</span> to
              confirm you would like to permanently delete your account.
            </DialogDescription>
            <form className="space-y-6" onSubmit={deleteUser}>
              <div className="grid gap-2">
                <Label htmlFor="email_confirmation" className="sr-only">
                  Email
                </Label>

                <Input
                  id="email_confirmation"
                  type="email"
                  name="email_confirmation"
                  ref={emailInput}
                  value={data.email_confirmation}
                  onChange={(e) =>
                    setData("email_confirmation", e.target.value)
                  }
                  placeholder="Enter your email address"
                  autoComplete="off"
                />

                <InputError message={errors.email_confirmation} />
              </div>

              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="secondary" onClick={closeModal}>
                    Cancel
                  </Button>
                </DialogClose>

                <Button
                  variant="destructive"
                  disabled={processing || !isEmailMatch}
                  asChild
                >
                  <button type="submit">Delete account</button>
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  )
}
